import React, {useState, useEffect, useContext} from 'react';
import { AnimatePresence, motion } from 'framer-motion';

/* Molecules */
import { Card, CardVehicle } from 'js/components/molecules';

/* Constants */
import { fadeInOut } from 'js/constants/animations';

/* Utilities */
import { getImageFraction, sortByKey, getLocaleUrl } from 'js/utilities';
import {getCheapestRentalSuffix} from "../../../utilities/getCheapestRentalSuffix";
import vehicleOverviewContext from "../../../context/VehicleOverviewContext";
import productPreviewContext from "../../../context/ProductPreviewContext";

export default function VehicleOverviewResults({
    specs = ['brandstof', 'transmissie', 'tellerstand'],
    vehicles = [],
    isLoading,
    banners = [],
    locale = 'nl',
    purchase_type = null,
    vehicleList,
    datalayerActiveFilters,
}) {

    const [showShortLeaseOrRentalPrices, setShowShortLeaseOrRentalPrices] = useState(false);
    const vOContext = useContext(vehicleOverviewContext) ? useContext(vehicleOverviewContext) : useContext(productPreviewContext) ? useContext(productPreviewContext) : null

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const aanschafvormArray = urlParams.getAll("aanschafvorm[]");

        const isShortLeaseSelected = aanschafvormArray.includes("short_lease");
        const isRentSelected = aanschafvormArray.includes("rent");

        setShowShortLeaseOrRentalPrices(isShortLeaseSelected || isRentSelected);
    }, [vehicles]);

    const [items, setItems] = useState([]);

    useEffect(() => {
        const newItems = vehicles.map((vehicle) => ({
            type: 'vehicle',
            vehicle: vehicle,
        }));

        sortByKey(banners, 'position').forEach((banner) => {
            if (newItems.length < (banner.position - 1)) return;

            newItems.splice((banner.position - 1), 0, {
                type: 'banner',
                banner: banner,
            });
        });

        setItems(newItems);
    }, [vehicles, banners]);

    const surchargeActiveConversionRuleMap = {
        'enriched_mourning_surcharge_set': 'mourning_surcharge_price',
        'enriched_transfer_surcharge_set': 'transfer_surcharge_price',
        'enriched_cooling_surcharge_set': 'cooling_surcharge_price',
        'enriched_multi_temp_surcharge_set': 'multi_temp_surcharge_price',
    }

    function getVehiclePrice(vehicle, discountPrice) {
        if (vOContext.useConversionVehicleList) {
            if (vOContext?.conversionRuleType) {
                const conversionTypeToShow = surchargeActiveConversionRuleMap[vOContext.conversionRuleType];
                const surchargePrice = vehicle?.enrichedValues[conversionTypeToShow];
                if (surchargePrice) {
                    return vehicle.price + (surchargePrice / 100)
                }
            }

            return vehicle?.enrichedValues?.conversion_vehicle_price
        }
        return vehicle?.enrichedValues?.partner_vehicle_price ? vehicle?.enrichedValues?.partner_vehicle_price : discountPrice || vehicle?.price
    }

    return (
        <div className="position-relative">
            <div className="row row--equalized gutters-2">
                {items.map((item, index) => {
                    if (item.type === 'vehicle') {
                        const { vehicle = {} } = item;
                        const discountPrice = vehicle?.attributes?.actieprijs?.value;
                        const url = new URL(getLocaleUrl(vehicle.url, locale), window.location.origin);
                        if (purchase_type) url.searchParams.set('purchase_type', purchase_type.toLowerCase().replaceAll(' ', '_'));
                        if (vOContext.useConversionVehicleList) url.searchParams.set('conversion', '1');
                        if (vOContext?.conversionRuleType) url.searchParams.set('conversion_type', surchargeActiveConversionRuleMap[vOContext.conversionRuleType]);
                        const isPartnerVehicle = vehicle.attributes?.partnervoertuig?.value ? vehicle.attributes.partnervoertuig.value : false;
                        if (vehicleList && vehicleList.id !== '') url.searchParams.set("vehicle_list_id", vehicleList.id);

                        return (
                            <div key={vehicle?.id || index} className="col-12 col-md-6 col-xxl-4">
                                <CardVehicle
                                    images={vehicle?.images?.map((image, imageIndex) => (
                                        {
                                            alt: `${vehicle?.brand} ${vehicle?.model} ${vehicle?.type}`,
                                            width: 1920,
                                            height: 1440,
                                            ...getImageFraction({
                                                path: image.path,
                                                ratio: 3 / 2,
                                                fraction: 1,
                                                breakpoints: {
                                                    md: {
                                                        fraction: 1 / 2,
                                                    },
                                                    xl: {
                                                        fraction: 1 / 3,
                                                    },
                                                    xxl: {
                                                        fraction: 1 / 4,
                                                    }
                                                }
                                            }),
                                            ...((index >= 1 || imageIndex >= 1) ? { loading: 'lazy' } : null),
                                        }
                                    ))}
                                    title={`${vehicle?.brand} ${vehicle?.model}`}
                                    text={vehicle?.type}
                                    specs={specs.map(key => {
                                        if (!vehicle?.attributes?.[key]?.value ) return null;

                                        let prefix = '', suffix = '', value = vehicle.attributes[key].value;

                                        if (key === 'tellerstand' || key === 'actieradius') {
                                            value = new Intl.NumberFormat('nl-NL', {}).format(value);
                                            suffix = ' km';
                                        }

                                        if (key === 'bijtelling_pct') {
                                            value = new Intl.NumberFormat('nl-NL', {}).format(value);
                                            suffix = ' %';
                                        }

                                        if (key === 'accu_capaciteit') {
                                            value = new Intl.NumberFormat('nl-NL', {}).format(value);
                                            suffix = ' KwH';
                                        }

                                        return `${prefix}${value}${suffix}`;
                                    }).filter((spec) => spec)}
                                    prices={[
                                        ...(showShortLeaseOrRentalPrices ? [
                                            ...(vehicle?.enrichedValues?.cheapest_rental_price > 0 ? [
                                                {
                                                    type: 'rent',
                                                    price: vehicle.enrichedValues.cheapest_rental_price,
                                                    suffix: getCheapestRentalSuffix(vehicle.enrichedValues.rental_period),
                                                    from: null,
                                                }
                                            ] : []),
                                            ...(vehicle?.attributes?.basis_short_lease?.value > 0 && vehicle?.enrichedValues?.has_short_lease_matrix ? [
                                                {
                                                    type: 'short_lease',
                                                    price: vehicle.attributes.basis_short_lease.value,
                                                    suffix: ' p/m',
                                                    from: null,
                                                }
                                            ] : []),
                                        ] : [
                                            ...(vehicle?.price > 0 || discountPrice > 0 ? [
                                                {
                                                    type: `purchase_${vehicle?.attributes?.btw_marge?.value?.toLowerCase() || 'btw'}`,
                                                    price: getVehiclePrice(vehicle, discountPrice),
                                                    suffix: '',
                                                    from: discountPrice ? vehicle?.price : null,
                                                    label: vOContext.useConversionVehicleList ? '(incl. ombouw)' : '',
                                                },
                                            ] : []),
                                            ...(vehicle?.enrichedValues?.finance_price > 0 && !vOContext.useConversionVehicleList ? [
                                                {
                                                    type: 'finance',
                                                    price: vehicle.enrichedValues.finance_price,
                                                    suffix: ' p/m',
                                                    from: null,
                                                }
                                            ] : []),
                                            ...(vehicle?.enrichedValues?.lease_price > 0 ? [
                                                {
                                                    type: 'operational_lease',
                                                    price: vOContext.useConversionVehicleList ? vehicle.enrichedValues?.conversion_lease_price : vehicle.enrichedValues.lease_price,
                                                    suffix: ' p/m',
                                                    from: null,
                                                }
                                            ] : []),
                                            ...(vehicle?.attributes?.basis_short_lease?.value > 0 && vehicle?.enrichedValues?.has_short_lease_matrix && !vOContext.useConversionVehicleList ? [
                                                {
                                                    type: 'short_lease',
                                                    price: vehicle.attributes.basis_short_lease.value,
                                                    suffix: ' p/m',
                                                    from: null,
                                                }
                                            ] : []),
                                            ...(vehicle?.enrichedValues?.cheapest_rental_price > 0 && !vOContext.useConversionVehicleList ? [
                                                {
                                                    type: 'rent',
                                                    price: vehicle.enrichedValues.cheapest_rental_price,
                                                    suffix: getCheapestRentalSuffix(vehicle.enrichedValues.rental_period),
                                                    from: null,
                                                }
                                            ] : []),
                                        ]),
                                    ]}
                                    button={{
                                        href: url.href,
                                    }}
                                    isPartnerVehicle={isPartnerVehicle}
                                    enrichedValues={vehicle?.enrichedValues}
                                    datalayerActiveFilters={datalayerActiveFilters}
                                />
                            </div>
                        )
                    }

                    if (item.type === 'banner') {
                        const { banner = {} } = item;

                        return (
                            <div key={index} className="col-12 col-md-6 col-xxl-4">
                                <Card
                                    className={`card--vehicle-banner bg-${banner.theme} ${banner.theme !== 'white' ? '' : 'card--bordered'}`}
                                    image={{
                                        alt: banner.title,
                                        width: 1920,
                                        height: 1440,
                                        ...getImageFraction({
                                            path: banner.image,
                                            ratio: 3 / 2,
                                            fraction: 1,
                                            breakpoints: {
                                                md: {
                                                    fraction: 1 / 2,
                                                },
                                                xl: {
                                                    fraction: 1 / 3,
                                                },
                                                xxl: {
                                                    fraction: 1 / 4,
                                                }
                                            }
                                        }),
                                        ...((index >= 1) ? { loading: 'lazy' } : null),
                                    }}
                                    title={banner.title}
                                    text={banner.text}
                                    button={{
                                        ...banner.button,
                                        href: banner.button?.url,
                                        className: 'btn-block stretched-link',
                                    }}
                                />
                            </div>
                        );
                    }

                    return null;
                })}
            </div>
            <AnimatePresence>
                {isLoading && (
                    <motion.div key="loader" className="loader--overlay" {...fadeInOut}>
                        <div className="icon icon--24 loader--icon"></div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
